import { EventDTO, FreightDTO, FromDTO, LegDTO, LocationDTO, RoadResponseDTO, ToDTO } from "./response.types";
import { RoadMessagesDTO, RoadStatusesDTO } from "entities/shared/roadCard.types";
import { CoordinateModelDTO, DateTimeModel, SearchReferenceDTO } from "entities/shared/shipmentCard.types";
import { ShipmentCardMetadataDTO } from "entities/shared/subscriptionModel";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";
import { t } from "shared/localization/localization";
import { piecesText } from "./utils";

export class SearchApiParser {
  private model: RoadResponseDTO;
  public readonly requestLimitReached: boolean = false;
  public readonly isSuccess: boolean = false;

  constructor(model: RoadResponseDTO) {
    this.model = model;
    this.requestLimitReached = this.model.status_code === RoadMessagesDTO.API_KEY_ACCESS_DENIED;
    this.isSuccess = this.model.status_code === RoadMessagesDTO.OK;
  }

  get metadata(): ShipmentCardMetadataDTO {
    return this.model.metadata;
  }

  get responseStatus(): ValueOf<typeof RoadMessagesDTO> {
    return this.model.status_code;
  }

  get number(): SearchReferenceDTO {
    return this.model.metadata.request_parameters.number;
  }

  get type(): string | null {
    return this.model.metadata.type ?? null;
  }

  get carrierCode(): string {
    return this.model.metadata.carrier?.carrier_code ?? "";
  }

  get scac(): string | null {
    return this.model.metadata?.carrier?.carrier_code || null;
  }

  get status(): ValueOf<typeof RoadStatusesDTO> {
    return this.model.data.status ?? RoadStatusesDTO.UNKNOWN;
  }

  get carrierName(): string {
    return this.model.metadata.carrier.name;
  }

  get pieces(): string {
    const piece = this.freight.quantity;
    const weight = this.freight.weight;
    if (piece && weight) {
      return `${piecesText(piece)} / ${weight} ${t("kg")}`;
    }
    if (piece) {
      return piecesText(piece);
    }
    if (weight) {
      return `${weight} ${t("kg")}`;
    }
    return "";
  }

  get fromLocation(): FromDTO {
    return this.model.data.from;
  }

  get toLocation(): ToDTO {
    return this.model.data.to;
  }

  get freight(): FreightDTO {
    return this.model.data.freight;
  }

  get locations(): LocationDTO[] {
    return this.model.data.locations;
  }

  get legs(): LegDTO[] {
    return this.model.data.legs ?? [];
  }

  get polCoordinate(): CoordinateModelDTO | null {
    const directionFrom = this.model.data.locations[0];
    if (!directionFrom?.lat || !directionFrom?.lng) return null;
    return {
      lat: directionFrom.lat,
      lng: directionFrom.lng,
    };
  }

  get podCoordinate(): CoordinateModelDTO | null {
    const directionTo = this.model.data?.locations[this.model.data.locations.length - 1];
    if (!directionTo.lat || !directionTo.lng) return null;
    return {
      lat: directionTo.lat,
      lng: directionTo.lng,
    };
  }

  get events(): EventDTO[] {
    return this.model.data.events ?? [];
  }

  get etd(): Nullable<DateTimeModel> {
    return this.model.data.from?.estimated_departure;
  }

  get atd(): Nullable<DateTimeModel> {
    return this.model.data.from?.actual_departure;
  }

  get eta(): Nullable<DateTimeModel> {
    return this.model.data.to?.estimated_arrival;
  }

  get ata(): Nullable<DateTimeModel> {
    return this.model.data.to?.actual_arrival;
  }
}
